import { forwardRef, type ReactNode, type SelectHTMLAttributes } from "react";
import { cx } from "./cx";
import { Field, type FieldSize } from "./Field";
import { IconChevronDown } from "./icons";
import "./Select.css";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  size?: FieldSize;
  /** Options; `<option>` children are rendered after these. */
  options?: readonly SelectOption[];
  /** Adds a first, empty-valued, disabled option shown when nothing is chosen. */
  placeholder?: string;
  fieldClassName?: string;
}

/** Native <select> (best keyboard/AT support) with the kit's skin. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    hint,
    error,
    size = "md",
    options,
    placeholder,
    id,
    required,
    disabled,
    className,
    fieldClassName,
    children,
    ...rest
  },
  ref,
) {
  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      id={id}
      required={required}
      disabled={disabled}
      className={fieldClassName}
    >
      {({ controlId, describedBy }) => (
        <div className="ui-select">
          <select
            ref={ref}
            id={controlId}
            className={cx("ui-control", `ui-control--${size}`, "ui-select__control", className)}
            required={required}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            {...rest}
          >
            {placeholder !== undefined ? (
              <option value="" disabled>
                {placeholder}
              </option>
            ) : null}
            {options?.map((o) => (
              <option key={o.value} value={o.value} disabled={o.disabled}>
                {o.label}
              </option>
            ))}
            {children}
          </select>
          <span className="ui-select__chevron" aria-hidden="true">
            <IconChevronDown />
          </span>
        </div>
      )}
    </Field>
  );
});
